import React, { useState, useEffect, useRef } from 'react';
import { X, Check, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { categories, Category } from "../data/categories";

// Multi-select version props
interface MultiSelectProps {
    selectedCategories: string[];
    onChange: (categories: string[]) => void;
    maxSelections?: number;
    mode?: 'multi';
    value?: never;
}

// Single-select version props (for backward compatibility)
interface SingleSelectProps {
    value: string;
    onChange: (value: string) => void;
    mode?: 'single';
    selectedCategories?: never;
}

type CategorySelectProps = MultiSelectProps | SingleSelectProps;

const CategorySelect: React.FC<CategorySelectProps> = (props) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredCategories, setFilteredCategories] = useState<Category[]>(categories);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Determine if we're in multi-select mode
    const isMultiSelect = 'selectedCategories' in props;

    useEffect(() => {
        const filtered = categories.filter((category) =>
            category.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredCategories(filtered);
    }, [searchTerm]);

    useEffect(() => {
        // Set initial search term for single select mode
        if (!isMultiSelect && 'value' in props) {
            setSearchTerm(props.value);
        }
    }, [isMultiSelect, props]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Multi-select handlers
    const toggleCategory = (categoryName: string) => {
        if (!isMultiSelect) return;
        
        const multiProps = props as MultiSelectProps;
        const { selectedCategories, onChange, maxSelections = 5 } = multiProps;
        
        if (selectedCategories.includes(categoryName)) {
            onChange(selectedCategories.filter(c => c !== categoryName));
        } else if (selectedCategories.length < maxSelections) {
            onChange([...selectedCategories, categoryName]);
        }
    };

    const removeCategory = (categoryName: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!isMultiSelect) return;
        
        const multiProps = props as MultiSelectProps;
        const { selectedCategories, onChange } = multiProps;
        onChange(selectedCategories.filter(c => c !== categoryName));
    };

    // Single-select handler
    const handleSingleSelect = (category: Category) => {
        if (isMultiSelect || !('onChange' in props)) return;
        
        const singleProps = props as SingleSelectProps;
        singleProps.onChange(category.name);
        setSearchTerm(category.name);
        setIsOpen(false);
    };

    // Single-select input change handler
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        if (!isMultiSelect && 'onChange' in props && !e.target.value) {
            const singleProps = props as SingleSelectProps;
            singleProps.onChange(e.target.value);
        }
    };

    if (isMultiSelect) {
        // Multi-select UI
        const multiProps = props as MultiSelectProps;
        const { selectedCategories, maxSelections = 5 } = multiProps;
        
        return (
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Categories
                </label>
                <div className="relative w-full" ref={dropdownRef}>
                    <div
                        className="min-h-[42px] p-2 border rounded-lg bg-white cursor-pointer flex flex-wrap gap-2 items-center"
                        onClick={() => setIsOpen(!isOpen)}
                    >
                        {selectedCategories.length === 0 ? (
                            <span className="text-gray-400">Select categories (max {maxSelections})</span>
                        ) : (
                            <>
                                {selectedCategories.map(category => (
                                    <span
                                        key={category}
                                        className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm"
                                    >
                                        {category}
                                        <X
                                            className="w-4 h-4 cursor-pointer hover:text-blue-600"
                                            onClick={(e) => removeCategory(category, e)}
                                        />
                                    </span>
                                ))}
                            </>
                        )}
                        <span className="ml-auto">
                            {isOpen ? (
                                <ChevronUp className="w-5 h-5 text-gray-400" />
                            ) : (
                                <ChevronDown className="w-5 h-5 text-gray-400" />
                            )}
                        </span>
                    </div>

                    {isOpen && (
                        <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg">
                            <div className="p-3 border-b">
                                <div className="relative">
                                    <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                                    <input
                                        type="text"
                                        className="w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                                        placeholder="Search categories..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ fontSize: '16px', width: 'calc(100% - 40px)', marginLeft: '10px', paddingLeft: '10px' }}
                                    />
                                </div>
                            </div>
                            <div className="max-h-60 overflow-y-auto">
                                {filteredCategories.map(category => (
                                    <div
                                        key={category.slug}
                                        className={`px-4 py-2 cursor-pointer flex items-center justify-between hover:bg-gray-100
                      ${selectedCategories.includes(category.name) ? 'bg-blue-50' : ''}`}
                                        onClick={() => toggleCategory(category.name)}
                                    >
                                        <span>{category.name}</span>
                                        {selectedCategories.includes(category.name) && (
                                            <Check className="w-4 h-4 text-blue-600" />
                                        )}
                                    </div>
                                ))}
                                {filteredCategories.length === 0 && (
                                    <div className="px-4 py-2 text-gray-500 text-center">
                                        No categories found
                                    </div>
                                )}
                            </div>
                            <div className="p-2 border-t bg-gray-50 text-sm text-gray-500">
                                Selected {selectedCategories.length} of {maxSelections} maximum
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    } else {
        // Single-select UI (backward compatible)
        return (
            <div className="position-relative" ref={dropdownRef}>
                <div className="input-group">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Enter category..."
                        value={searchTerm}
                        onChange={handleInputChange}
                        onFocus={() => setIsOpen(true)}
                    />
                </div>

                {isOpen && (
                    <div
                        className="position-absolute w-100 mt-1 bg-white border rounded shadow-sm"
                        style={{ maxHeight: "300px", overflowY: "auto", zIndex: 1000 }}
                    >
                        {filteredCategories.map((category) => (
                            <div
                                key={category.slug}
                                className="p-2 cursor-pointer hover:bg-light"
                                onClick={() => handleSingleSelect(category)}
                            >
                                <div className="d-flex align-items-center">
                                    <span>{category.name}</span>
                                </div>
                            </div>
                        ))}
                        {filteredCategories.length === 0 && (
                            <div className="p-2 text-muted">No categories found</div>
                        )}
                    </div>
                )}
            </div>
        );
    }
};

// Export both as named and default export for compatibility
export { CategorySelect };
export default CategorySelect;